import type { IInvestorRepository } from "@/application/ports/repositories";
import type {
	CompanyInvestor,
	CreateCompanyInvestorRequest,
	CreateIndividualInvestorRequest,
	IndividualInvestor,
	InvestorListParams,
	InvestorExportParams,
	InvestorDocumentType,
	InvestorDocumentResponse,
	AggregatedDocumentsResponse,
	InvestorListResponse,
	InvestorMetrics,
	InvestorOperationResponse,
	InvestorStatus,
	InvestorType,
	UpdateCompanyInvestorRequest,
	UpdateIndividualInvestorRequest,
} from "@/domain/investor";
import type { OrganizationInvitation } from "@/domain/invitation";
import { downloadBlob } from "../fileDownload";
import { apiClient, ApiClientError } from "../client";

// Response shape for document download signed URLs
interface DocumentResponse {
	signed_url: string;
	name?: string;
	filename?: string;
	content_type?: string;
	size_bytes?: number;
	uploaded_at?: string;
}

export class InvestorRepository implements IInvestorRepository {
	async list(params: InvestorListParams = {}): Promise<InvestorListResponse> {
		const searchParams = new URLSearchParams();
		if (params.status) searchParams.append("status", params.status);
		if (params.investor_type)
			searchParams.append("investor_type", params.investor_type);
		if (params.search) searchParams.append("search", params.search);
		if (params.uuid) searchParams.append("uuid", params.uuid);
		if (params.sort_by) searchParams.append("sort_by", params.sort_by);
		if (params.sort_direction)
			searchParams.append("sort_direction", params.sort_direction);
		if (params.limit) searchParams.append("limit", params.limit.toString());
		if (params.offset) searchParams.append("offset", params.offset.toString());

		const response = await apiClient.get<InvestorListResponse>(
			`/investors?${searchParams.toString()}`,
		);
		return response.data;
	}

	async getMetrics(params?: {
		status?: InvestorStatus;
		investor_type?: InvestorType;
		search?: string;
	}): Promise<InvestorMetrics> {
		const searchParams = new URLSearchParams();
		if (params?.status) searchParams.append("status", params.status);
		if (params?.investor_type)
			searchParams.append("investor_type", params.investor_type);
		if (params?.search) searchParams.append("search", params.search);

		const query = searchParams.toString();
		const url = query ? `/investors/metrics?${query}` : "/investors/metrics";
		const response = await apiClient.get<InvestorMetrics>(url);
		return response.data;
	}

	async createIndividual(
		data: CreateIndividualInvestorRequest,
	): Promise<IndividualInvestor> {
		const response = await apiClient.post<IndividualInvestor>(
			"/investors/individuals",
			data,
		);
		return response.data;
	}

	async getIndividual(uuid: string): Promise<IndividualInvestor> {
		const response = await apiClient.get<IndividualInvestor>(
			`/investors/individuals/${uuid}`,
		);
		return response.data;
	}

	async updateIndividual(
		uuid: string,
		data: UpdateIndividualInvestorRequest,
	): Promise<IndividualInvestor> {
		const response = await apiClient.patch<IndividualInvestor>(
			`/investors/individuals/${uuid}`,
			data,
		);
		return response.data;
	}

	async createCompany(
		data: CreateCompanyInvestorRequest,
	): Promise<CompanyInvestor> {
		const response = await apiClient.post<CompanyInvestor>(
			"/investors/companies",
			data,
		);
		return response.data;
	}

	async getCompany(uuid: string): Promise<CompanyInvestor> {
		const response = await apiClient.get<CompanyInvestor>(
			`/investors/companies/${uuid}`,
		);
		return response.data;
	}

	async updateCompany(
		uuid: string,
		data: UpdateCompanyInvestorRequest,
	): Promise<CompanyInvestor> {
		const response = await apiClient.patch<CompanyInvestor>(
			`/investors/companies/${uuid}`,
			data,
		);
		return response.data;
	}

	async delete(uuid: string): Promise<void> {
		await apiClient.delete(`/investors/${uuid}`);
	}

	async activate(uuid: string): Promise<InvestorOperationResponse> {
		const response = await apiClient.patch<InvestorOperationResponse>(
			`/investors/${uuid}/activate`,
		);
		return response.data;
	}

	async deactivate(uuid: string): Promise<InvestorOperationResponse> {
		const response = await apiClient.patch<InvestorOperationResponse>(
			`/investors/${uuid}/deactivate`,
		);
		return response.data;
	}

	async inviteToHub(
		uuid: string,
		lang: string = "es",
	): Promise<OrganizationInvitation> {
		const response = await apiClient.post<OrganizationInvitation>(
			`/investors/${uuid}/invite?lang=${encodeURIComponent(lang)}`,
		);
		return response.data;
	}

	async listDocuments(
		investorUuid: string,
		includeInvestments: boolean = true,
		docType?: InvestorDocumentType,
	): Promise<AggregatedDocumentsResponse> {
		const searchParams = new URLSearchParams();
		searchParams.append("include_investments", includeInvestments.toString());
		if (docType) searchParams.append("doc_type", docType);

		const response = await apiClient.get<AggregatedDocumentsResponse>(
			`/investors/${investorUuid}/documents?${searchParams.toString()}`,
		);
		return response.data;
	}

	async uploadDocument(
		investorUuid: string,
		file: File,
		docType: InvestorDocumentType,
		title?: string,
	): Promise<InvestorDocumentResponse> {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("doc_type", docType);
		if (title) formData.append("title", title);

		const response = await apiClient.post<InvestorDocumentResponse>(
			`/investors/${investorUuid}/documents`,
			formData,
		);
		return response.data;
	}

	async getDocument(
		investorUuid: string,
		documentUuid: string,
	): Promise<InvestorDocumentResponse> {
		const response = await apiClient.get<InvestorDocumentResponse>(
			`/investors/${investorUuid}/documents/${documentUuid}`,
		);
		return response.data;
	}

	async getDocumentUrl(
		investorUuid: string,
		documentUuid: string,
	): Promise<DocumentResponse> {
		const response = await apiClient.get<DocumentResponse>(
			`/investors/${investorUuid}/documents/${documentUuid}/download`,
		);
		return response.data;
	}

	async downloadDocument(
		investorUuid: string,
		documentUuid: string,
		fileName?: string,
	): Promise<void> {
		const documentResponse = await this.getDocumentUrl(
			investorUuid,
			documentUuid,
		);

		const actualFileName =
			documentResponse.filename ||
			documentResponse.name ||
			fileName ||
			`document-${documentUuid}.pdf`;

		const response = await fetch(documentResponse.signed_url, {
			method: "GET",
			mode: "cors",
			credentials: "omit",
			headers: { Accept: "application/pdf" },
		});

		if (!response.ok) {
			throw new ApiClientError({
				message: `Failed to download document: ${response.status} ${response.statusText}`,
				code: "DOWNLOAD_ERROR",
				status: response.status,
			});
		}

		const blob = await response.blob();
		const blobUrl = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = blobUrl;
		link.download = actualFileName;
		document.body.appendChild(link);
		link.click();

		setTimeout(() => {
			document.body.removeChild(link);
			window.URL.revokeObjectURL(blobUrl);
		}, 100);
	}

	async deleteDocument(
		investorUuid: string,
		documentUuid: string,
	): Promise<void> {
		await apiClient.delete(
			`/investors/${investorUuid}/documents/${documentUuid}`,
		);
	}

	async exportToCsv(params?: InvestorExportParams): Promise<void> {
		const queryParams = new URLSearchParams();
		if (params) {
			if (params.search) queryParams.append("search", params.search);
			if (params.status) queryParams.append("status", params.status);
			if (params.investor_type)
				queryParams.append("investor_type", params.investor_type);
			if (params.sort_by) queryParams.append("sort_by", params.sort_by);
			if (params.sort_direction)
				queryParams.append("sort_direction", params.sort_direction);
		}

		const url = queryParams.toString()
			? `/investors/export?${queryParams.toString()}`
			: "/investors/export";

		const { blob, filename } = await apiClient.getBlobWithFilename(
			url,
			"investors.csv",
			{
				headers: { Accept: "text/csv" },
			},
		);

		downloadBlob(blob, filename);
	}
}
